import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { User } from '../types';
import { authAPI, adminContentAPI, blogAPI } from '../services/api';
// HTML file upload utilities
const readHtmlFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};


interface Tutorial {
  id: number;
  title: string;
  description: string | null;
  youtube_url: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  category: string;
  display_order: number;
  is_published: boolean;
  created_at: string | null;
}

interface BlogPostItem {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content?: string;
  cover_image_url: string | null;
  category: string;
  tags: string | null;
  is_published: boolean;
  created_at: string | null;
}

const AdminContent: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'tutorials' | 'blogs' | 'maintenance'>('tutorials');
  const [loading, setLoading] = useState(true);

  // Tutorials state
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [tutorialForm, setTutorialForm] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState<Tutorial | null>(null);
  const [tutForm, setTutForm] = useState({
    title: '', description: '', youtube_url: '', category: 'Getting Started', display_order: 0, is_published: false,
  });
  const [tutSaving, setTutSaving] = useState(false);

  // Blog state
  const [blogs, setBlogs] = useState<BlogPostItem[]>([]);
  const [blogForm, setBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState<BlogPostItem | null>(null);
  const [blogFormData, setBlogFormData] = useState({
    title: '', content: '', excerpt: '', cover_image_url: '', category: 'Charts & Graphs', tags: '', is_published: false,
  });
  const [blogSaving, setBlogSaving] = useState(false);
  const [blogPreview, setBlogPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Feedback
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Maintenance: vendor changelog review
  const [changelogText, setChangelogText] = useState('');
  const [vendor, setVendor] = useState('FMP');
  const [customVendor, setCustomVendor] = useState('');
  const [reviewRunning, setReviewRunning] = useState(false);
  const [reviewMarkdown, setReviewMarkdown] = useState<string | null>(null);
  // The text the report was based on (credentials redacted server-side) and the
  // vendor it was run for — a past report's vendor, not the current dropdown.
  const [reviewSource, setReviewSource] = useState<string>('');
  const [reviewVendor, setReviewVendor] = useState<string>('');
  const [maintReports, setMaintReports] = useState<{ id: string; vendor: string; created_at: string | null }[]>([]);

  // Vendors NWC has a code-level dependency registry for → grounded review.
  // Anything else (Railway, Cloudflare, custom) gets a best-effort generic review.
  // Must match the VENDORS keys in backend services/vendor_registries.py.
  const GROUNDED_VENDORS = ['FMP', 'Stripe', 'Anthropic', 'Postmark'];
  const VENDOR_OPTIONS = [...GROUNDED_VENDORS, 'Railway', 'Cloudflare', 'Other'];
  const effectiveVendor = vendor === 'Other' ? customVendor.trim() : vendor;
  const isGrounded = GROUNDED_VENDORS.includes(effectiveVendor);

  const loadTutorials = useCallback(async () => {
    const res = await adminContentAPI.listTutorials();
    setTutorials(res.data);
  }, []);

  const loadBlogs = useCallback(async () => {
    const res = await adminContentAPI.listBlogs();
    setBlogs(res.data);
  }, []);

  const loadMaintReports = useCallback(async () => {
    const res = await adminContentAPI.listMaintenanceReports();
    setMaintReports(res.data);
  }, []);

  const showReport = (r: { report_markdown: string; source_text?: string; vendor: string }) => {
    setReviewMarkdown(r.report_markdown);
    setReviewSource(r.source_text || '');
    setReviewVendor(r.vendor);
  };

  // Report + the submitted text, so whoever receives the file (usually Claude Code)
  // can check the findings against the evidence. The fence is one backtick longer
  // than any run inside the source, so pasted markdown can't close it early.
  const fullReport = (): string => {
    if (!reviewMarkdown) return '';
    if (!reviewSource) return reviewMarkdown;
    const longestRun = Math.max(2, ...(reviewSource.match(/`+/g) || []).map(s => s.length));
    const fence = '`'.repeat(longestRun + 1);
    return `${reviewMarkdown}\n\n## Source as submitted\n\n${fence}text\n${reviewSource}\n${fence}\n`;
  };

  const runChangelogReview = async () => {
    if (!changelogText.trim() || !effectiveVendor) return;
    setReviewRunning(true);
    setReviewMarkdown(null);
    try {
      const res = await adminContentAPI.reviewChangelog({ changelog_text: changelogText, vendor: effectiveVendor });
      showReport(res.data);
      setMessage({ type: 'success', text: 'Changelog review complete.' });
      await loadMaintReports();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Review failed.' });
    } finally {
      setReviewRunning(false);
    }
  };

  const viewMaintReport = async (id: string) => {
    try {
      const res = await adminContentAPI.getMaintenanceReport(id);
      showReport(res.data);
    } catch {
      setMessage({ type: 'error', text: 'Could not load report.' });
    }
  };

  const downloadMarkdown = (markdown: string) => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (reviewVendor || effectiveVendor || 'vendor').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    a.download = `${slug}-changelog-review-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const userRes = await authAPI.getCurrentUser();
        if (!userRes.data.is_admin) {
          navigate('/dashboard');
          return;
        }
        setUser(userRes.data);
        await Promise.all([loadTutorials(), loadBlogs(), loadMaintReports()]);
      } catch (err: any) {
        if (err.response?.status === 401) navigate('/login');
        else navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [navigate, loadTutorials, loadBlogs, loadMaintReports]);

  // ── Tutorial CRUD ───────────────────────────────────────

  const openTutorialForm = (tutorial?: Tutorial) => {
    if (tutorial) {
      setEditingTutorial(tutorial);
      setTutForm({
        title: tutorial.title,
        description: tutorial.description || '',
        youtube_url: tutorial.youtube_url || '',
        category: tutorial.category,
        display_order: tutorial.display_order,
        is_published: tutorial.is_published,
      });
    } else {
      setEditingTutorial(null);
      setTutForm({ title: '', description: '', youtube_url: '', category: 'Getting Started', display_order: 0, is_published: false });
    }
    setTutorialForm(true);
  };

  const saveTutorial = async () => {
    setTutSaving(true);
    try {
      if (editingTutorial) {
        await adminContentAPI.updateTutorial(editingTutorial.id, tutForm);
        setMessage({ type: 'success', text: 'Tutorial updated!' });
      } else {
        await adminContentAPI.createTutorial(tutForm);
        setMessage({ type: 'success', text: 'Tutorial created!' });
      }
      setTutorialForm(false);
      await loadTutorials();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save tutorial' });
    } finally {
      setTutSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const deleteTutorial = async (id: number) => {
    if (!window.confirm('Delete this tutorial?')) return;
    try {
      await adminContentAPI.deleteTutorial(id);
      setMessage({ type: 'success', text: 'Tutorial deleted' });
      await loadTutorials();
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete tutorial' });
    }
    setTimeout(() => setMessage(null), 4000);
  };

  // ── Blog CRUD ───────────────────────────────────────────

  const openBlogForm = async (post?: BlogPostItem) => {
    if (post) {
      // Fetch full content for editing
      try {
        const res = await blogAPI.get(post.slug);
        setEditingBlog(post);
        setBlogPreview(false);
        setBlogFormData({
          title: res.data.title,
          content: res.data.content,
          excerpt: res.data.excerpt || '',
          cover_image_url: res.data.cover_image_url || '',
          category: res.data.category,
          tags: res.data.tags || '',
          is_published: res.data.is_published,
        });
      } catch {
        setMessage({ type: 'error', text: 'Failed to load blog post for editing' });
        return;
      }
    } else {
      setEditingBlog(null);
      setBlogFormData({
        title: '', content: '', excerpt: '', cover_image_url: '', category: 'Charts & Graphs', tags: '', is_published: false,
      });
      setBlogPreview(false);
    }
    setBlogForm(true);
  };

  const saveBlog = async () => {
    if (!blogFormData.title.trim() || !blogFormData.content.trim()) {
      setMessage({ type: 'error', text: 'Title and content are required' });
      setTimeout(() => setMessage(null), 4000);
      return;
    }
    setBlogSaving(true);
    try {
      if (editingBlog) {
        await adminContentAPI.updateBlog(editingBlog.id, blogFormData);
        setMessage({ type: 'success', text: 'Blog post updated!' });
      } else {
        await adminContentAPI.createBlog(blogFormData);
        setMessage({ type: 'success', text: 'Blog post created!' });
      }
      setBlogForm(false);
      await loadBlogs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save blog post' });
    } finally {
      setBlogSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const deleteBlog = async (id: number) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await adminContentAPI.deleteBlog(id);
      setMessage({ type: 'success', text: 'Blog post deleted' });
      await loadBlogs();
    } catch {
      setMessage({ type: 'error', text: 'Failed to delete blog post' });
    }
    setTimeout(() => setMessage(null), 4000);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/login');
  };

  // ── Render ──────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const tutorialCategories = ['Getting Started', 'Dashboard', 'Watchlist', 'Portfolio', 'Stocks', 'Technical Analysis', 'DCF Valuation', 'Alerts', 'General'];
  const blogCategories = ['Charts & Graphs', 'Technical Analysis', 'Fundamental Analysis', 'Portfolio Strategy', 'Getting Started', 'Feature Guides', 'Market Insights', 'General'];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-800">
      {/* SEO — React 19 hoists to <head> */}
      <title>Admin — NWC-Analytics</title>
      <meta name="description" content="NWC-Analytics content administration." />
      <link rel="canonical" href="https://nwc-analytics.com/admin" />
      <NavBar currentPage="admin" user={user} onLogout={handleLogout} />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Panel</h1>
        </div>

        {/* Feedback */}
        {message && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
            message.type === 'success'
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tabs */}
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 w-fit mb-6">
          <button
            onClick={() => { setActiveTab('tutorials'); setTutorialForm(false); setBlogForm(false); }}
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              activeTab === 'tutorials'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Tutorials ({tutorials.length})
          </button>
          <button
            onClick={() => { setActiveTab('blogs'); setTutorialForm(false); setBlogForm(false); }}
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              activeTab === 'blogs'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Blog Posts ({blogs.length})
          </button>
          <button
            onClick={() => { setActiveTab('maintenance'); setTutorialForm(false); setBlogForm(false); }}
            className={`px-6 py-2 text-sm font-medium transition-colors ${
              activeTab === 'maintenance'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Maintenance
          </button>
        </div>

        {/* ════════════════════════════════════════════════════
            TUTORIALS TAB
           ════════════════════════════════════════════════════ */}
        {activeTab === 'tutorials' && (
          <div>
            {!tutorialForm && (
              <button
                onClick={() => openTutorialForm()}
                className="mb-4 inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Add Tutorial
              </button>
            )}

            {/* Tutorial Form */}
            {tutorialForm && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {editingTutorial ? 'Edit Tutorial' : 'New Tutorial'}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                    <input
                      type="text"
                      value={tutForm.title}
                      onChange={e => setTutForm({ ...tutForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., How to Set Up Your Watchlist"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">YouTube URL *</label>
                    <input
                      type="text"
                      value={tutForm.youtube_url}
                      onChange={e => setTutForm({ ...tutForm, youtube_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                    <textarea
                      value={tutForm.description}
                      onChange={e => setTutForm({ ...tutForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Brief description of what this tutorial covers..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                    <select
                      value={tutForm.category}
                      onChange={e => setTutForm({ ...tutForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {tutorialCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={tutForm.display_order}
                      onChange={e => setTutForm({ ...tutForm, display_order: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="tut-published"
                      checked={tutForm.is_published}
                      onChange={e => setTutForm({ ...tutForm, is_published: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <label htmlFor="tut-published" className="text-sm text-gray-700 dark:text-gray-300">Published (visible to users)</label>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-5">
                  <button
                    onClick={saveTutorial}
                    disabled={tutSaving || !tutForm.title.trim()}
                    className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium text-sm transition-colors"
                  >
                    {tutSaving ? 'Saving...' : (editingTutorial ? 'Update Tutorial' : 'Create Tutorial')}
                  </button>
                  <button
                    onClick={() => setTutorialForm(false)}
                    className="px-5 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Tutorials List */}
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {tutorials.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No tutorials yet. Click "Add Tutorial" to create one.</td></tr>
                  ) : tutorials.map(t => (
                    <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-600/50">
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{t.display_order}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{t.title}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{t.category}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          t.is_published
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        }`}>
                          {t.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openTutorialForm(t)} className="text-primary-600 dark:text-primary-400 hover:underline text-sm mr-3">Edit</button>
                        <button onClick={() => deleteTutorial(t.id)} className="text-red-500 dark:text-red-400 hover:underline text-sm">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════
            BLOG POSTS TAB
           ════════════════════════════════════════════════════ */}
        {activeTab === 'blogs' && (
          <div>
            {!blogForm && (
              <button
                onClick={() => openBlogForm()}
                className="mb-4 inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                New Blog Post
              </button>
            )}

            {/* Blog Editor Form */}
            {blogForm && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {editingBlog ? 'Edit Blog Post' : 'New Blog Post'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                    <input
                      type="text"
                      value={blogFormData.title}
                      onChange={e => setBlogFormData({ ...blogFormData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Understanding Candlestick Charts"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                      <select
                        value={blogFormData.category}
                        onChange={e => setBlogFormData({ ...blogFormData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {blogCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma-separated)</label>
                      <input
                        type="text"
                        value={blogFormData.tags}
                        onChange={e => setBlogFormData({ ...blogFormData, tags: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="RSI, MACD, Moving Averages"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cover Image URL</label>
                      <input
                        type="text"
                        value={blogFormData.cover_image_url}
                        onChange={e => setBlogFormData({ ...blogFormData, cover_image_url: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="https://..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Excerpt (preview text)</label>
                    <textarea
                      value={blogFormData.excerpt}
                      onChange={e => setBlogFormData({ ...blogFormData, excerpt: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="A short preview that appears on the blog listing..."
                    />
                  </div>

                  {/* HTML Content Editor */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content *</label>

                    {/* Toolbar: Upload + Edit/Preview toggle */}
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".html,.htm"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const html = await readHtmlFile(file);
                            setBlogFormData({ ...blogFormData, content: html });
                            setMessage({ type: 'success', text: `Loaded ${file.name} (${(html.length / 1024).toFixed(1)} KB)` });
                            setTimeout(() => setMessage(null), 4000);
                          } catch {
                            setMessage({ type: 'error', text: 'Failed to read HTML file' });
                            setTimeout(() => setMessage(null), 4000);
                          }
                          // Reset so the same file can be re-uploaded
                          e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                        Upload HTML
                      </button>

                      <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                        <button
                          type="button"
                          onClick={() => setBlogPreview(false)}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                            !blogPreview
                              ? 'bg-primary-600 text-white'
                              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          Code
                        </button>
                        <button
                          type="button"
                          onClick={() => setBlogPreview(true)}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                            blogPreview
                              ? 'bg-primary-600 text-white'
                              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          Preview
                        </button>
                      </div>

                      {blogFormData.content && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                          {(blogFormData.content.length / 1024).toFixed(1)} KB
                        </span>
                      )}
                    </div>

                    {/* Code Editor or Live Preview */}
                    {!blogPreview ? (
                      <textarea
                        value={blogFormData.content}
                        onChange={e => setBlogFormData({ ...blogFormData, content: e.target.value })}
                        rows={18}
                        spellCheck={false}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-900 text-gray-100 font-mono text-sm leading-relaxed focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                        placeholder="Paste or upload your HTML content..."
                      />
                    ) : (
                      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white" style={{ minHeight: '400px' }}>
                        {blogFormData.content ? (
                          <iframe
                            srcDoc={blogFormData.content}
                            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                            className="w-full border-0"
                            style={{ minHeight: '400px', height: '60vh' }}
                            title="Blog preview"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-64 text-gray-400 dark:text-gray-500 text-sm">
                            No content to preview — upload an HTML file or paste HTML in the Code tab.
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="blog-published"
                      checked={blogFormData.is_published}
                      onChange={e => setBlogFormData({ ...blogFormData, is_published: e.target.checked })}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <label htmlFor="blog-published" className="text-sm text-gray-700 dark:text-gray-300">Published (visible to users)</label>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-5">
                  <button
                    onClick={saveBlog}
                    disabled={blogSaving || !blogFormData.title.trim() || !blogFormData.content.trim()}
                    className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-5 py-2 rounded-lg font-medium text-sm transition-colors"
                  >
                    {blogSaving ? 'Saving...' : (editingBlog ? 'Update Post' : 'Publish Post')}
                  </button>
                  <button
                    onClick={() => setBlogForm(false)}
                    className="px-5 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Blog Posts List */}
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {blogs.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No blog posts yet. Click "New Blog Post" to write one.</td></tr>
                  ) : blogs.map(b => (
                    <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-600/50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{b.title}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{b.category}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          b.is_published
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                        }`}>
                          {b.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {b.created_at ? new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openBlogForm(b)} className="text-primary-600 dark:text-primary-400 hover:underline text-sm mr-3">Edit</button>
                        <button onClick={() => deleteBlog(b.id)} className="text-red-500 dark:text-red-400 hover:underline text-sm">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════
            MAINTENANCE TAB — vendor changelog review
           ════════════════════════════════════════════════════ */}
        {activeTab === 'maintenance' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Vendor Changelog Review</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Pick the vendor, then paste their changelog (e.g. from an update email). Claude reviews it and
                returns a markdown report flagging anything that may need a code change — download it and hand it
                to Claude Code. For FMP, Stripe, Anthropic, and Postmark it checks against NWC's actual
                code dependencies; for any other vendor it does a best-effort review against NWC's stack.
              </p>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vendor</label>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={vendor}
                    onChange={e => setVendor(e.target.value)}
                    className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  >
                    {VENDOR_OPTIONS.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                  {vendor === 'Other' && (
                    <input
                      type="text"
                      value={customVendor}
                      onChange={e => setCustomVendor(e.target.value)}
                      maxLength={50}
                      placeholder="Vendor name (e.g. Railway, Cloudflare)…"
                      className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm flex-1 min-w-[14rem]"
                    />
                  )}
                </div>
                {effectiveVendor && (
                  <p className={`mt-1.5 text-xs ${isGrounded ? 'text-teal-600 dark:text-teal-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {isGrounded
                      ? `Grounded review — checked against NWC's ${effectiveVendor} code dependencies.`
                      : `Best-effort review — no dependency registry for ${effectiveVendor}; findings are based on NWC's stack and need manual verification.`}
                  </p>
                )}
              </div>

              <textarea
                value={changelogText}
                onChange={e => setChangelogText(e.target.value)}
                rows={10}
                placeholder={`Paste the ${effectiveVendor || 'vendor'} changelog text here…`}
                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono"
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={runChangelogReview}
                  disabled={reviewRunning || !changelogText.trim() || !effectiveVendor}
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                >
                  {reviewRunning ? 'Reviewing…' : 'Run Claude review'}
                </button>
                {changelogText && (
                  <button
                    onClick={() => { setChangelogText(''); setReviewMarkdown(null); }}
                    className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {reviewMarkdown && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Review report</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(fullReport())}
                      className="text-sm px-3 py-1.5 rounded border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      Copy
                    </button>
                    <button
                      onClick={() => downloadMarkdown(fullReport())}
                      className="text-sm px-3 py-1.5 rounded bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      Download .md
                    </button>
                  </div>
                </div>
                <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800 rounded p-4 max-h-[32rem] overflow-auto font-mono">
                  {reviewMarkdown}
                </pre>
                {reviewSource && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                      Submitted text ({reviewSource.length.toLocaleString()} chars, credentials redacted)
                    </summary>
                    <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded p-4 max-h-[24rem] overflow-auto font-mono">
                      {reviewSource}
                    </pre>
                  </details>
                )}
              </div>
            )}

            {maintReports.length > 0 && (
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-lg border dark:border-gray-500 p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Past reviews</h3>
                <div className="space-y-2">
                  {maintReports.map(r => (
                    <div key={r.id} className="flex items-center justify-between gap-3 text-sm border-b border-gray-100 dark:border-gray-600 pb-2 last:border-0">
                      <span className="text-gray-700 dark:text-gray-300">
                        {r.vendor} — {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                      </span>
                      <button
                        onClick={() => viewMaintReport(r.id)}
                        className="text-teal-600 dark:text-teal-400 hover:underline shrink-0"
                      >
                        View
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContent;